/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUserApiToken } from "../../slices/currentUser/currentUserSlice";
import { m11ResearchStudyResourceChanged, selectM11ResearchStudyResource } from "../../slices/m11ReportAuthoringTool/m11ReportAuthoringToolSlice";
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import jsonEquals from "../../functions/jsonEquals";
import CodeableConceptEntry from "../../datatypeComponents/CodeableConceptEntry";
import StringEntry from "../../datatypeComponents/StringEntry";
import ArrayEntry from "../../datatypeComponents/ArrayEntry";
import PositiveIntEntry from "../../datatypeComponents/PositiveIntEntry";
import CodeEntry from "../../datatypeComponents/CodeEntry";
import { convertValueSetToCodeableConceptSet } from "../../dictionaries/THOandFHIRValueSets";
import saveM11Changes from "../../functions/saveM11ChangesAPI";
import { selectM11ResearchStudyFoi } from "../../slices/m11StepCount/m11StepCountSlice";
import { validateStep } from "../../functions/validateM11ReportAPI";

const saveOverallDesign2Changes = async (dispatch, apiToken, m11ResearchStudyFoi, overallDesignState,
    setAlertModalState, setStartingOverallDesignState) => {

    let changesStates = {
        overallDesignState: overallDesignState
    };
    let response = await saveM11Changes(apiToken, 14, m11ResearchStudyFoi, changesStates);
    if (response?.success) {
        if (response.m11ResearchStudyResource) {
            dispatch(m11ResearchStudyResourceChanged(response.m11ResearchStudyResource));
        }
        setStartingOverallDesignState(JSON.parse(JSON.stringify(overallDesignState)));
    } else {
        if (response.error) {
            alert(response.error);
        }
    }
};

const blindedRolesValueSet = [
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C142710",
        "display": "Participant (C142710)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C17445",
        "display": "Care Provider (C17445)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C25936",
        "display": "Investigator (C25936)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C207599",
        "display": "Outcomes Assessor (C207599)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C70793",
        "display": "Sponsor (C70793)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C48660",
        "display": "Not Applicable (C48660)"
    }
];

const independentCommitteeValueSet = [
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C142578",
        "display": "Independent Data Monitoring Committee (C142578)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C215671",
        "display": "Dose Escalation Committee (C215671)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C78726",
        "display": "Endpoint Adjudication Committee (C78726)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C17649",
        "display": "Other Independent Committee (C17649)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C41132",
        "display": "None (for Independent Committees) (C41132)"
    }
];

const trialBlindSchemaValueSet = [
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C15228",
        "display": "Double Blind (C15228)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C187674",
        "display": "Observer Blind (C187674)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C49659",
        "display": "Open Label (C49659)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C28233",
        "display": "Single Blind (C28233)"
    }
];

const M11Step014EnterOverallDesign2 = forwardRef(({ theM11ResearchStudyFoi, setAlertModalState }, ref) => {

    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const m11ResearchStudyResource = useSelector(selectM11ResearchStudyResource);
    const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

    const [overallDesignState, setOverallDesignState] = useState({
        numberOfArms: "", trialBlindSchema: "", blindedRoles: [],
        targetNumber: "", targetOrMaximum: "", assignedOrEnrolled: "",
        durationIntervention: "", durationParticipation: "", durationAdditional: "",
        independentCommittee: [], otherCommittees: ""
    });
    const [startingOverallDesignState, setStartingOverallDesignState] = useState({
        numberOfArms: "", trialBlindSchema: "", blindedRoles: [],
        targetNumber: "", targetOrMaximum: "", assignedOrEnrolled: "",
        durationIntervention: "", durationParticipation: "", durationAdditional: "",
        independentCommittee: [], otherCommittees: ""
    });

    useImperativeHandle(ref, () => ({
        unsavedChanges: (m11ResearchStudyResource && !jsonEquals(overallDesignState, startingOverallDesignState)),
        async saveAllChanges() {
            await saveOverallDesign2Changes(dispatch, apiToken, m11ResearchStudyFoi, overallDesignState,
                setAlertModalState, setStartingOverallDesignState);
        }
    }));

    useEffect(() => {
        if (m11ResearchStudyResource) {
            let startingData = {
                numberOfArms: "", trialBlindSchema: "", blindedRoles: [],
                targetNumber: "", targetOrMaximum: "", assignedOrEnrolled: "",
                durationIntervention: "", durationParticipation: "", durationAdditional: "",
                independentCommittee: [], otherCommittees: ""
            };
            if (m11ResearchStudyResource.recruitment?.targetNumber) {
                startingData.targetNumber = m11ResearchStudyResource.recruitment.targetNumber;
            }
            if (m11ResearchStudyResource.classifier?.length > 0) {
                for (const classifier of m11ResearchStudyResource.classifier) {
                    if (classifier.coding?.[0]?.system === "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl") {
                        if (classifier.coding[0].code === "C15228" ||
                            classifier.coding[0].code === "C187674" ||
                            classifier.coding[0].code === "C49659" ||
                            classifier.coding[0].code === "C28233") {
                            startingData.trialBlindSchema = classifier;
                        } else if (classifier.coding[0].code === "C142578") {
                            startingData.independentCommittee.push(classifier);
                        } else if (classifier.coding[0].code === "C215671") {
                            startingData.independentCommittee.push(classifier);
                        } else if (classifier.coding[0].code === "C78726") {
                            startingData.independentCommittee.push(classifier);
                        } else if (classifier.coding[0].code === "C41132" &&
                            classifier.coding[0].display === "None (for Independent Committees) (C41132)"
                        ) {
                            startingData.independentCommittee.push(classifier);
                        } else if (classifier.coding[0].code === "C17649" &&
                            classifier.coding[0].display === "Other Independent Committee (C17649)") {
                            startingData.independentCommittee.push(classifier);
                        }
                    } else if (classifier.coding?.[0]?.system === "ForUseWithM11ReportAuthoringTool") {
                        if (classifier.coding[0].display === "Number of Arms") {
                            startingData.numberOfArms = parseInt(classifier.text);
                        } else if (classifier.coding[0].display === "Target or Maximum") {
                            startingData.targetOrMaximum = classifier.text;
                        } else if (classifier.coding[0].display === "Assigned or Enrolled") {
                            startingData.assignedOrEnrolled = classifier.text;
                        } else if (classifier.coding[0].display === "Duration of Intervention") {
                            startingData.durationIntervention = classifier.text;
                        } else if (classifier.coding[0].display === "Duration of Participation") {
                            startingData.durationParticipation = classifier.text;
                        } else if (classifier.coding[0].display === "Additional Duration Comment") {
                            startingData.durationAdditional = classifier.text;
                        } else if (classifier.coding[0].display === "Other Committees") {
                            startingData.otherCommittees = classifier.text;
                        }
                    }
                }
            }
            if (m11ResearchStudyResource.extension?.length) {
                for (const extension of m11ResearchStudyResource.extension) {
                    if (extension.url === "http://hl7.org/fhir/StructureDefinition/blinded-role" &&
                        extension.valueCodeableConcept) {
                        startingData.blindedRoles.push(extension.valueCodeableConcept);
                    }
                }
            }
            startingData.loaded = true;
            setOverallDesignState(startingData);
            setStartingOverallDesignState(startingData);
        }
    }, [m11ResearchStudyResource]);

    return <div>{overallDesignState?.loaded && <div>
        <h3>Enter Overall Design Details part 2</h3>
        <h4 style={{ marginBottom: "0px" }}>Enter Number of Arms</h4>
        <PositiveIntEntry elementName='numberOfArms' fieldLabel='Number of Arms'
            startingValue={overallDesignState.numberOfArms}
            setResourceState={setOverallDesignState} />
        <h4 style={{ marginBottom: "0px" }}>Select Trial Blind Schema</h4>
        <CodeableConceptEntry elementName='trialBlindSchema' fieldLabel='Trial Blind Schema'
            noWriteIn={true}
            codeableConceptLevelValueSet={[
                {
                    "coding": [{
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C15228",
                        "display": "Double Blind (C15228)"
                    }]
                },
                {
                    "coding": [{
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C187674",
                        "display": "Observer Blind (C187674)"
                    }]
                },
                {
                    "coding": [{
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C49659",
                        "display": "Open Label (C49659)"
                    }]
                },
                {
                    "coding": [{
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C28233",
                        "display": "Single Blind (C28233)"
                    }]
                }
            ]}
            startingValue={overallDesignState.trialBlindSchema}
            setResourceState={setOverallDesignState} />
        <h4 style={{ marginBottom: "0px" }}>enter any of the following Blinded Roles which indicate who will not be made aware of the treatment group during the trial.</h4>
        <div>
            <p>A selection of 'Not Applicable' indicates an open-label trial.</p>
            {!jsonEquals(overallDesignState, {
                numberOfArms: "", trialBlindSchema: "", blindedRoles: [],
                targetNumber: "", targetOrMaximum: "", assignedOrEnrolled: "",
                durationIntervention: "", durationParticipation: "", durationAdditional: "",
                independentCommittee: [], otherCommittees: ""
            }) &&
                <ArrayEntry datatype='CodeableConcept'
                    elementName='blindedRoles' fieldLabel='Blinded Roles' noWriteIn={true} doNotShowFieldLabel={true}
                    codeableConceptLevelValueSet={convertValueSetToCodeableConceptSet(blindedRolesValueSet)}
                    startingValue={overallDesignState.blindedRoles}
                    setResourceState={setOverallDesignState} />}
        </div>
        <h4 style={{ marginBottom: "0px" }}>Enter the expected Number of Participants</h4>
        <div>
            <PositiveIntEntry elementName='targetNumber' fieldLabel='Expected Number of Participants'
                startingValue={overallDesignState.targetNumber}
                setResourceState={setOverallDesignState} />
            <p>Indicate whether the number provided is the <b>target</b> or <b>maximum</b> number of individuals to be assigned or enrolled.</p>
            <CodeEntry elementName='targetOrMaximum' fieldLabel='Target or Maximum'
                allowedValues={["Target", "Maximum"]}
                startingValue={overallDesignState.targetOrMaximum}
                setResourceState={setOverallDesignState} />
            <p>Indicate whetehr the expected number of participants is to be <b>randomly assigned to trial intervention</b> or <b>enrolled</b>.</p>
            <CodeEntry elementName='assignedOrEnrolled' fieldLabel='Randomly Assigned or Enrolled'
                allowedValues={["randomly assigned to trial intervention", "enrolled"]}
                startingValue={overallDesignState.assignedOrEnrolled}
                setResourceState={setOverallDesignState} />
        </div>
        <h4 style={{ marginBottom: "0px" }}>Enter Duration for each participant.</h4>
        <div>
            <p>Enter the total planned duration of trial <b>intervention</b> for each participant.</p>
            <StringEntry elementName='durationIntervention' fieldLabel='Duration of Trial Intervention'
                startingValue={overallDesignState.durationIntervention}
                setResourceState={setOverallDesignState} />
            <p>Enter the total planned duration of trial <b>participation</b> for each participant.</p>
            <StringEntry elementName='durationParticipation' fieldLabel='Duration of Trial Participation'
                startingValue={overallDesignState.durationParticipation}
                setResourceState={setOverallDesignState} />
            <p>If necessary, include any clarifications or cross references to details in the main body of the protocol in the optional field Additional Description of Duration.</p>
            <StringEntry elementName='durationAdditional' fieldLabel='Additional Description of Duration'
                startingValue={overallDesignState.durationAdditional}
                setResourceState={setOverallDesignState} />
        </div>
        <h4 style={{ marginBottom: "0px" }}>Indicate any Committee(s) that will be reviewing data while the trial is ongoing</h4>
        <div>
            <p>Select any Independent Committees that apply. You may select more than one. If you select 'Other', add detail. If none apply, select 'None'.</p>
            {!jsonEquals(overallDesignState, {
                numberOfArms: "", trialBlindSchema: "", blindedRoles: [],
                targetNumber: "", targetOrMaximum: "", assignedOrEnrolled: "",
                durationIntervention: "", durationParticipation: "", durationAdditional: "",
                independentCommittee: [], otherCommittees: ""
            }) &&
                <ArrayEntry datatype='CodeableConcept'
                    elementName='independentCommittee' fieldLabel='Independent Committee' doNotShowFieldLabel={true}
                    codeableConceptLevelValueSet={convertValueSetToCodeableConceptSet(independentCommitteeValueSet)}
                    startingValue={overallDesignState.independentCommittee}
                    setResourceState={setOverallDesignState} />}
            <p>If applicable, describe Other Committees.</p>
            <StringEntry elementName='otherCommittees' fieldLabel='Other Committees'
                startingValue={overallDesignState.otherCommittees}
                setResourceState={setOverallDesignState} />
        </div>
    </div >
    }</div>
});

// eslint-disable-next-line no-empty-pattern
const M11Step014EnterOverallDesignValidationCheck = ({ }) => {

  const apiToken = useSelector(selectCurrentUserApiToken);
  const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

  const [validationCheckState, setValidationCheckState] = useState({});

  useEffect(() => {
    if (m11ResearchStudyFoi) {
      validateStep(apiToken, 14, m11ResearchStudyFoi,
        setValidationCheckState)
    }
  }, [m11ResearchStudyFoi]);

  return <div>{validationCheckState?.success && <div>
        <h4>Step 14 (Enter Overall Design Details part 2) {validationCheckState.step14numberOfChecks} issues checked. {validationCheckState.step14numberPassed} passed. {validationCheckState.step14numberFailed} failed.</h4>
        <p>Number of Arms is present and is a numeric value: {validationCheckState.resultCheckNumberOfArms}</p>
        <p>Trial Blind Schema is present and matches an allowed value: {validationCheckState.resultCheckTrialBlindSchema}</p>
        <p>Blinded Roles is present and matches allowed values: {validationCheckState.resultCheckBlindedRoles}</p>
        <p>Expected Number of Participants is present and is a numeric value: {validationCheckState.resultCheckNumberOfParticipants}</p>
        <p>Target or Maximum is selected for Expected Number of Participants: {validationCheckState.resultCheckTargetOrMaximum}</p>
        <p>Randomly assigned or Enrolled is selected for Expected Number of Participants: {validationCheckState.resultCheckAssignedOrEnrolled}</p>
        <p>Duration of trial Intervention is present: {validationCheckState.resultCheckDurationIntervention}</p>
        <p>Duration of trial Participation is present: {validationCheckState.resultCheckDurationParticipation}</p>
        <p>Independent Committees is present and matches allowed values: {validationCheckState.resultCheckIndependentCommittees}</p>
        <p>If Other Independent Committee is present, explanatory text is provided: {validationCheckState.resultCheckOtherIndependentCommitteeExplained}</p>
    </div>}  </div>
}

const M11Step014EnterOverallDesign2HelperText = <div>
    <h3>Enter Overall Design Details part 2</h3>
    <div style={{ textAlign: "left" }}>
        <p>For Number of Arms, enter the numeric value for the number of arms in the trial. For trials with a different number of arms in different periods, populate this field based on the total number of arms.</p>
        <p>For Trial Blind Schema,  enter one of the following values: Double Blind (C15228), Observer Blind (C187674), Open Label (C49659), Single Blind (C28233). For designs in which these details may differ in one or more trial periods, answer according to the portion of the trial in which the highest number of blinded roles occurs. Additional details can be provided in Section 6.7.3 Measures to Maintain Blinding.</p>
        <p>For Blinded Roles, enter any of the following which indicate who will not be made aware of the treatment group during the trial: Participant (C142710); Care Provider (C17445); Investigator (C25936); Outcomes Assessor (C207599); Sponsor (C70793); Not Applicable (C48660). A selection of 'Not Applicable' indicates an open-label trial.</p>
        <p>For Number of Participants, state the expected number of participants to be assigned to trial intervention/enrolled. Indicate whether the number provided is the target or maximum number of individuals to be randomly assigned to trial intervention/enrolled. This will be stated as 'A [Target/Maximum] of [Enter Number of Participants] participants will be [randomly assigned to trial intervention/enrolled].</p>
        <p>For Duration: </p>
        <div style={{ marginLeft: "24px" }}>
            <p>Enter the total planned duration of trial <b>intervention</b> for each participant.</p>
            <p>Enter the total planned duration of trial <b>participation</b> for each participant.</p>
            <p>Note that the total duration of trial participation should include any washout and any follow-up periods in which the participant is not receiving trial intervention. When duration will vary, provide a short explanation (for example, “event-driven” or “adaptive design”).</p>
            <p>If necessary, include any clarifications or cross references to details in the main body of the protocol in the optional field Additional Description of Duration.</p>
        </div>
        <p>For Committees, Indicate whether any committee(s) will be reviewing data while the trial is ongoing, and the type of committee.</p>
        <div style={{ marginLeft: "24px" }}>
            <p>Common examples include Data Monitoring Committee, Dose Escalation Committee, or Endpoint Adjudication Committee; describe others, if applicable. List independent committees in the space indicated.</p>
            <p>Other committees may be included at the Sponsor’s discretion in the separate space provided.</p>
            <p>Committees listed here should be fully described in Section 11.4 Committees.</p>
        </div>
        <p>Click the 'Save All Changes' button.</p>
    </div>
</div >

export default M11Step014EnterOverallDesign2;
export {
    M11Step014EnterOverallDesign2HelperText, M11Step014EnterOverallDesignValidationCheck, trialBlindSchemaValueSet,
    blindedRolesValueSet, independentCommitteeValueSet
};